<template>
    <pendo-modal
        :visible="visible"
        :title="title"
        height="auto"
        :width="width"
        :action-buttons="actionButtons"
        @close="dismiss"
        @closed="startTrialLoading = false">
        <template #body>
            <div v-if="canStartTrial">
                <div
                    v-if="startTrialLoading"
                    class="free-trial--loading">
                    <pendo-loading-indicator size="large" />
                </div>
                <div
                    v-show="!startTrialLoading"
                    class="free-trial--form-wrapper">
                    <p class="free-trial--form-preamble">
                        You may only enable the free, 14-day full trial one time. After the trial has ended, the
                        upgraded features will be disabled.
                    </p>
                    <pendo-checkbox
                        v-model="acknowledgmentChecked"
                        data-cy="free-trial-acknowledgment-checkbox"
                        label="I understand that I can only enable this trial for my account one time." />
                </div>
            </div>
            <div v-else>
                To unlock your free trial of premium features, you must first install the Pendo snippet.
            </div>
        </template>
    </pendo-modal>
</template>

<script>
import { EventBus } from '@utils/event-bus.js';
import { PendoModal, PendoLoadingIndicator, PendoCheckbox, PendoNotification } from '@pendo/components-vue3';

import { mapActions as mapPiniaActions, mapState as mapPiniaState } from 'pinia';

import { useSubscriptionStore } from '@/stores/subscription.store';
import { useAuthStore } from '@/stores/auth.store';

export default {
    name: 'FreeTrialForm',
    components: {
        PendoModal,
        PendoLoadingIndicator,
        PendoCheckbox,
    },
    props: {
        isInstalled: {
            type: Boolean,
            required: true,
        },
        subscriptionSetupKey: {
            type: String,
            default: '',
        },
    },
    data () {
        return {
            visible: false,
            startTrialLoading: false,
            acknowledgmentChecked: false,
        };
    },
    computed: {
        ...mapPiniaState(useAuthStore, {
            hasSegmentFlag: 'hasSegmentFlag',
        }),
        canStartTrial () {
            return this.isInstalled || this.hasSegmentFlag('upfrontTrial');
        },
        disableSubmitButton () {
            return this.startTrialLoading || !this.acknowledgmentChecked;
        },
        actionButtons () {
            if (this.canStartTrial) {
                return [
                    {
                        'type': 'secondary',
                        'label': 'Cancel',
                        'handler': this.dismiss,
                        'data-cy': 'free-trial-cancel-button',
                    },
                    {
                        'type': 'primary',
                        'disabled': this.disableSubmitButton,
                        'label': 'Start Trial Now',
                        'handler': this.startSendProcess,
                        'data-cy': 'start-free-trial-button',
                    },
                ];
            }

            return [
                {
                    'type': 'secondary',
                    'label': 'Cancel',
                    'handler': this.dismiss,
                    'data-cy': 'start-free-trial-install-pendo-cancel-button',
                },
                {
                    'type': 'primary',
                    'label': 'Install Pendo',
                    'handler': this.goToSetup,
                    'data-cy': 'start-free-trial-install-pendo-button',
                },
            ];
        },
        title () {
            if (this.canStartTrial) {
                return 'Start 14-day trial?';
            }

            return 'Looks like you need to install!';
        },
        width () {
            if (this.canStartTrial) {
                return '420px';
            }

            return '500px';
        },
    },
    created () {
        EventBus.$on('openFreeTrialForm', this.showModal);
    },
    beforeUnmount () {
        EventBus.$off('openFreeTrialForm', this.showModal);
    },
    methods: {
        ...mapPiniaActions(useSubscriptionStore, {
            startFreeTrial: 'startFreeTrial',
        }),
        showModal () {
            this.visible = true;
        },
        dismiss () {
            this.visible = false;
            this.removeTrialFormQueryParam();
        },
        goToSetup () {
            window.location.href = this.$router.pendo.toPath({
                name: 'Setup',
                query: { setupKey: this.subscriptionSetupKey },
            });
        },
        async startSendProcess () {
            this.startTrialLoading = true;
            try {
                await this.makeRequestToStartFreeTrial();
            } catch (error) {
                console.error(error);
                this.startTrialLoading = false;
            }
            window.pendo.track('Free Trial Started', {
                duration: 14,
                upfront: false,
                trialStartLocation: 'free trial form',
                startedBy: 'user',
            });
            this.openConfirmationGuide('PLG_Trial Enabled_Success');
        },
        async makeRequestToStartFreeTrial () {
            try {
                await this.startFreeTrial({
                    checklist: !this.isInstalled && 'preinstallOnboarding',
                });
                this.dismiss();
            } catch (error) {
                const errorResponse = error.response;
                if (errorResponse.status === 400 || errorResponse.status === 403) {
                    this.dismiss();
                    PendoNotification({
                        type: 'error',
                        title: 'This subscription is not eligible for the trial.',
                        duration: 3000,
                    });
                } else if (errorResponse.status === 500) {
                    PendoNotification({
                        type: 'error',
                        title: 'Something went wrong!',
                        message: 'There was an error starting the trial. Please try again.',
                        duration: 3000,
                    });
                }
            } finally {
                this.startTrialLoading = false;
            }
        },
        removeTrialFormQueryParam () {
            const queryCopy = Object.assign({}, this.$route.query);

            if (!queryCopy.trialForm) return;

            delete queryCopy.trialForm;

            this.$router.replace({
                query: queryCopy,
            });
        },
    },
};
</script>

<style lang="less" scoped>
.free-trial {
    &--form-wrapper {
        display: flex;
        width: 100%;
        flex-direction: column;
        justify-content: center;

        .free-trial--form-preamble {
            font-family: Proxima Nova;
            padding: 0;
            line-height: 17px;
            margin-bottom: 18px;
        }
    }

    &--loading {
        display: flex;
        height: 100%;
        width: 100%;
        flex-direction: column;
        align-items: center;
        justify-content: center;
    }
}
</style>
